import * as THREE from 'three';
import type {FaceId} from '@hyperlight/shared';
import type {PiecePrim} from './bodyModels';
import type {HairRigDef} from './hairRig';
import {HAIR_HEAD_FITS} from './hairFit.generated';
import {bodyShapeMorphs} from './bodyShapeMorphs';
import {MALE_RIG} from './maleRig';
import {FEMALE_RIG} from './femaleRig';
import {rigProfile} from './rigProfile';

const smooth=(a:number,b:number,x:number):number=>{const t=THREE.MathUtils.clamp((x-a)/(b-a),0,1);return t*t*(3-2*t);};
const profiles={male:rigProfile(MALE_RIG),female:rigProfile(FEMALE_RIG)};
const worlds={male:new Map(MALE_RIG.map(([n],i)=>[n,profiles.male.world[i]])),female:new Map(FEMALE_RIG.map(([n],i)=>[n,profiles.female.world[i]]))};
const fitted=new WeakMap<THREE.BufferGeometry,Map<string,THREE.BufferGeometry>>();

/** Transport the hair's authored clearance over the measured neutral skull.
 * The hanging ends ease back to the shared body; scalp roots follow the head.
 * This never edits a face, a shared source, or another avatar's color buffers. */
export function fitHairPoint(p:THREE.Vector3,body:'male'|'female',face:FaceId):THREE.Vector3{
 if(face==='base')return p;
 const table=HAIR_HEAD_FITS.bodies[body],offsets=table.offsets[face as keyof typeof table.offsets];
 if(!offsets)return p;
 const [cx,cy,cz]=table.center,dx=p.x-cx,dy=p.y-cy,dz=p.z-cz;
 const horizontal=Math.hypot(dx,dz),radius=Math.hypot(horizontal,Math.max(0,dy));
 if(radius<.001)return p;
 const angle=(Math.atan2(dx,dz)+Math.PI*2)%(Math.PI*2),elevation=Math.max(0,Math.atan2(dy,horizontal));
 const {azimuths,elevations}=HAIR_HEAD_FITS,az=angle/(Math.PI*2)*azimuths,a=Math.floor(az),af=az-a;
 let row=0;while(row<elevations.length-2&&elevation>elevations[row+1])row++;
 const ef=THREE.MathUtils.clamp((elevation-elevations[row])/(elevations[row+1]-elevations[row]),0,1);
 const sample=(r:number):number=>THREE.MathUtils.lerp(offsets[r*azimuths+a],offsets[r*azimuths+(a+1)%azimuths],af);
 const distance=THREE.MathUtils.lerp(sample(row),sample(row+1),ef)*smooth(1.45,1.60,p.y);
 p.x+=dx/radius*distance;p.y+=Math.max(0,dy)/radius*distance;p.z+=dz/radius*distance;
 return p;
}

/** Cache one immutable template per hairstyle/head pair, including fitted motion guides. */
export function fitHairToHead(prims:PiecePrim[],body:'male'|'female',face:FaceId):PiecePrim[]{
 if(face==='base')return prims;
 const key=`${body}:${face}`,v=new THREE.Vector3(),q=new THREE.Vector3();
 const columns=[new THREE.Vector3(),new THREE.Vector3(),new THREE.Vector3()];
 const jacobian=new THREE.Matrix3(),normalMatrix=new THREE.Matrix3(),h=1e-5;
 return prims.map(prim=>{
  const source=prim.geometry;let cache=fitted.get(source);const hit=cache?.get(key);
  if(hit)return{...prim,geometry:hit};
  const geo=source.clone(),p=source.getAttribute('position'),norm=source.getAttribute('normal');
  geo.userData={...source.userData};
  const out=geo.getAttribute('position'),outNorm=geo.getAttribute('normal');
  for(let i=0;i<p.count;i++){
   v.fromBufferAttribute(p,i);q.copy(v);fitHairPoint(q,body,face);out.setXYZ(i,q.x,q.y,q.z);
   for(let axis=0;axis<3;axis++){
    q.copy(v).setComponent(axis,v.getComponent(axis)-h);fitHairPoint(q,body,face);
    columns[axis].copy(v).setComponent(axis,v.getComponent(axis)+h);fitHairPoint(columns[axis],body,face).sub(q).multiplyScalar(.5/h);
   }
   const [a,b,c]=columns;jacobian.set(a.x,b.x,c.x,a.y,b.y,c.y,a.z,b.z,c.z);normalMatrix.copy(jacobian).invert().transpose();
   q.fromBufferAttribute(norm,i).applyMatrix3(normalMatrix).normalize();outNorm.setXYZ(i,q.x,q.y,q.z);
  }
  const rig=source.userData.hairRig as HairRigDef|undefined;
  if(rig)geo.userData.hairRig={...rig,chains:rig.chains.map(c=>({...c,points:c.points.map(p=>fitHairPoint(new THREE.Vector3(...p),body,face).toArray())}))};
  const shapes=bodyShapeMorphs(out.array as Float32Array,worlds[body],body,outNorm.array as Float32Array);
  for(const shape of shapes){
   const i=geo.morphAttributes.position?.findIndex(a=>a.name===shape.name)??-1;
   if(i>=0){const attr=new THREE.BufferAttribute(shape.delta,3);attr.name=shape.name;geo.morphAttributes.position[i]=attr;}
  }
  geo.computeBoundingBox();geo.computeBoundingSphere();geo.userData.sharedTemplate=true;geo.userData.hairHeadFit=key;
  if(!cache)fitted.set(source,cache=new Map());cache.set(key,geo);
  return{...prim,geometry:geo};
 });
}
